import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Frequency, RecurrenceRule, Weekday, WeekOrdinal } from '@/api/planned';
import { useLocale } from '@/i18n/useLocale';
import {
  describeRecurrence,
  FREQUENCIES,
  endingOf,
  formOf,
  ruleWithDayForm,
  ruleWithEnding,
  ruleWithFrequency,
  type DayForm,
  type Ending,
  localeWeekdayOrder,
  ORDINALS,
  validateRule,
  weekdayName,
  monthName,
} from './recurrence';
import { SubmitButton } from '@/components/SubmitButton';

/** The title's id: the host dialog's accessible name while the recurrence form shows. */
export const RECURRENCE_TITLE_ID = 'recurrence-title';
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

export type RecurrenceMode = 'series' | 'meeting' | 'planLine';

interface RecurrencePanelProps {
  draft: RecurrenceRule;
  setDraft: Dispatch<SetStateAction<RecurrenceRule>>;
  mode: RecurrenceMode;
  /** Cancel: the host decides whether a dirty draft asks first (`useDialogDirtyGuard`). */
  onCancel: () => void;
  /** Save: hands the (valid) draft to the caller. */
  onApply: () => void;
  /** The panel replaced a form in an open dialog: focus moves to its first control. */
  focusFirst?: boolean;
}

/**
 * The content of the recurrence dialog — title, the rule's fields, Cancel and Save — for a host to
 * place in its own `GuardedDialog`. The draft is the host's, so its guard sees what is dirty.
 */
export function RecurrencePanel({
  draft,
  setDraft,
  mode,
  onCancel,
  onApply,
  focusFirst = false,
}: RecurrencePanelProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const error = validateRule(draft);
  const ending = endingOf(draft);
  const dayForm = formOf(draft);
  const weekdays = localeWeekdayOrder(locale);
  const needsDays =
    draft.frequency === 'MONTHLY' ||
    draft.frequency === 'QUARTERLY' ||
    draft.frequency === 'YEARLY';
  // Replacing a form in a dialog that stays open leaves focus on a button that has just unmounted.
  const frequencyRef = useRef<{ focus: () => void }>(null);
  useEffect(() => {
    if (focusFirst) frequencyRef.current?.focus();
  }, [focusFirst]);

  function patch(next: Partial<RecurrenceRule>) {
    setDraft((prev) => ({ ...prev, ...next }));
  }

  function handleFrequency(frequency: Frequency) {
    setDraft((prev) => ruleWithFrequency(prev, frequency));
  }

  function handleEnding(next: Ending) {
    setDraft((prev) => ruleWithEnding(prev, next));
  }

  function handleDayForm(next: DayForm) {
    setDraft((prev) => ruleWithDayForm(prev, next));
  }

  function toggleDay(day: number) {
    const current = draft.byMonthDays ?? [];
    const next = current.includes(day)
      ? current.filter((d) => d !== day)
      : [...current, day].sort((a, b) => a - b);
    patch({ byMonthDays: next });
  }

  return (
    <>
      <DialogTitle id={RECURRENCE_TITLE_ID}>{t(`recurrence.title.${mode}`)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <FormControl fullWidth>
            <InputLabel id="recurrence-freq">{t('recurrence.repeats')}</InputLabel>
            <Select
              inputRef={frequencyRef}
              labelId="recurrence-freq"
              label={t('recurrence.repeats')}
              value={draft.frequency}
              onChange={(event) => handleFrequency(event.target.value as Frequency)}
            >
              {FREQUENCIES.map((f) => (
                <MenuItem key={f} value={f}>
                  {t(`recurrence.freq.${f}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {draft.frequency !== 'ONCE' && (
            <TextField
              type="number"
              label={t('recurrence.everyN')}
              value={draft.interval ?? 1}
              onChange={(event) => patch({ interval: Number(event.target.value) })}
              slotProps={{ htmlInput: { min: 1, step: 1 } }}
              helperText={t(`recurrence.freq.${draft.frequency}`)}
            />
          )}

          {draft.frequency === 'WEEKLY' && (
            <FormControl>
              <FormLabel>{t('recurrence.weekdays')}</FormLabel>
              <ToggleButtonGroup
                value={draft.byWeekdays ?? []}
                onChange={(_event, next: Weekday[]) => patch({ byWeekdays: next })}
                aria-label={t('recurrence.weekdays')}
              >
                {weekdays.map((day) => (
                  <ToggleButton key={day} value={day} aria-label={weekdayName(day, locale, 'long')}>
                    {weekdayName(day, locale, 'narrow')}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </FormControl>
          )}

          {needsDays && (
            <FormControl>
              <RadioGroup
                value={dayForm}
                onChange={(_event, next) => handleDayForm(next as DayForm)}
              >
                <FormControlLabel value="days" control={<Radio />} label={t('recurrence.onDays')} />
                <FormControlLabel
                  value="ordinal"
                  control={<Radio />}
                  label={t('recurrence.onWeekday')}
                />
              </RadioGroup>
            </FormControl>
          )}

          {needsDays && dayForm === 'days' && (
            <Box>
              <Stack direction="row" sx={{ gap: 0.5, flexWrap: 'wrap' }}>
                {DAYS.map((day) => (
                  <Chip
                    key={day}
                    size="small"
                    label={day}
                    color={(draft.byMonthDays ?? []).includes(day) ? 'primary' : 'default'}
                    variant="filled"
                    onClick={() => toggleDay(day)}
                    aria-pressed={(draft.byMonthDays ?? []).includes(day)}
                  />
                ))}
              </Stack>
              {(draft.byMonthDays ?? []).includes(31) && (
                <FormHelperText>{t('recurrence.day31Help')}</FormHelperText>
              )}
            </Box>
          )}

          {needsDays && dayForm === 'ordinal' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1.5 }}>
              <FormControl fullWidth>
                <InputLabel id="recurrence-ord">{t('recurrence.ordinalLabel')}</InputLabel>
                <Select
                  labelId="recurrence-ord"
                  label={t('recurrence.ordinalLabel')}
                  value={draft.byWeekOrdinal ?? 'FIRST'}
                  onChange={(event) => patch({ byWeekOrdinal: event.target.value as WeekOrdinal })}
                >
                  {ORDINALS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {t(`recurrence.ordinal.${o}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="recurrence-owd">{t('recurrence.weekdayLabel')}</InputLabel>
                <Select
                  labelId="recurrence-owd"
                  label={t('recurrence.weekdayLabel')}
                  value={draft.byOrdinalWeekday ?? 'MON'}
                  onChange={(event) => patch({ byOrdinalWeekday: event.target.value as Weekday })}
                >
                  {weekdays.map((d) => (
                    <MenuItem key={d} value={d}>
                      {weekdayName(d, locale, 'long')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}

          {draft.frequency === 'YEARLY' && (
            <FormControl fullWidth>
              <InputLabel id="recurrence-month">{t('recurrence.month')}</InputLabel>
              <Select
                labelId="recurrence-month"
                label={t('recurrence.month')}
                value={draft.byMonth ?? Number(draft.startOn.slice(5, 7))}
                onChange={(event) => patch({ byMonth: Number(event.target.value) })}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <MenuItem key={m} value={m}>
                    {monthName(m, locale)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            type="date"
            label={t('recurrence.startsOn')}
            value={draft.startOn}
            onChange={(event) => patch({ startOn: event.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />

          <FormControl>
            <FormLabel>{t('recurrence.ends')}</FormLabel>
            <RadioGroup value={ending} onChange={(_event, next) => handleEnding(next as Ending)}>
              <FormControlLabel value="never" control={<Radio />} label={t('recurrence.never')} />
              <FormControlLabel value="on" control={<Radio />} label={t('recurrence.endsOn')} />
              <FormControlLabel
                value="after"
                control={<Radio />}
                label={t('recurrence.endsAfter')}
              />
            </RadioGroup>
          </FormControl>
          {ending === 'on' && (
            <TextField
              type="date"
              label={t('recurrence.endsOn')}
              value={draft.endsOn ?? ''}
              onChange={(event) => patch({ endsOn: event.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          )}
          {ending === 'after' && (
            <TextField
              type="number"
              label={t('recurrence.endsAfter')}
              value={draft.occurrenceCount ?? 1}
              onChange={(event) => patch({ occurrenceCount: Number(event.target.value) })}
              slotProps={{ htmlInput: { min: 1, step: 1 } }}
            />
          )}

          <Typography variant="body2">{describeRecurrence(draft, locale, t)}</Typography>
          {error !== null && <Alert severity="error">{t(`recurrence.error.${error}`)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
        {/* Applies the draft to the caller: no request of its own, so no loading state. */}
        <SubmitButton disabled={error !== null} onClick={onApply}>
          {t('common.save')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
